import { useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatError, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [f, setF] = useState({ email: "", password: "", name: "", org_name: "", role: params.get("role") || "sponsor" });
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  if (user) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "login" ? { email: f.email, password: f.password } : f;
      const { data } = await api.post(`/auth/${mode}`, body);
      setUser(data);
      navigate("/portal");
    } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };

  return (
    <div className="sky-bg min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="login-page">
      <div className="w-full max-w-md rounded-3xl bg-white/90 backdrop-blur-xl border border-white shadow-xl p-8">
        <img src="/brand/logo.webp" alt="Swim for Rakim" className="w-20 mx-auto" />
        <h1 className="font-display uppercase text-3xl text-[#03045E] text-center mt-3">{mode === "login" ? "Partner Login" : "Create Portal Account"}</h1>
        <p className="text-sm text-slate-500 text-center mt-1">For sponsors, fence partners, swim providers, volunteers and admins.</p>
        <form onSubmit={submit} className="space-y-3 mt-6">
          {mode === "register" && <>
            <div><Label>I am a…</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="register-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["sponsor", "fence_partner", "swim_provider", "volunteer"].map((r) => <SelectItem key={r} value={r} data-testid={`register-role-${r}`}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" />
            {f.role !== "volunteer" && <Input placeholder="Organization / company" value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} data-testid="register-org-input" />}
          </>}
          <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
          <Input required type="password" minLength={mode === "register" ? 8 : 1} placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
          <button disabled={busy} className="w-full rounded-full bg-[#03045E] hover:bg-[#0077B6] text-white py-3 font-bold uppercase flex items-center justify-center gap-2 transition-colors duration-200" data-testid="login-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{mode === "login" ? "Sign in" : "Create account"}</button>
        </form>
        {mode === "register" && <p className="text-xs text-slate-500 mt-3">New accounts are reviewed and approved by Swim for Rakim before portal data is unlocked.</p>}
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm font-bold text-[#0077B6] mt-5" data-testid="login-toggle-mode">{mode === "login" ? "New partner? Create an account" : "Already have an account? Sign in"}</button>
      </div>
    </div>
  );
}
